import "react-notion/src/styles.css";
import "prismjs/themes/prism-tomorrow.css";
import { NotionRenderer } from "react-notion";

import React, { useState, useEffect } from 'react';

// bookmark 블록 커스텀 렌더러
const BookmarkBlock = ({ blockValue }) => {
  const link = blockValue?.properties?.link?.[0]?.[0] || '';
  const title = blockValue?.properties?.title?.[0]?.[0] || link;
  const description = blockValue?.properties?.description?.[0]?.[0] || '';
  const cover = blockValue?.format?.bookmark_cover || '';
  const icon = blockValue?.format?.bookmark_icon || '';

  if (!link) return null;

  return (
    <a href={link} target="_blank" rel="noopener noreferrer"
       style={{ textDecoration: 'none', color: 'inherit', display: 'block', margin: '8px 0' }}>
      <div style={{
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
      }}>
        <div style={{ flex: 1, padding: '14px 16px', minWidth: 0 }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
          {description && (
            <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.4',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {description}
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#999', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {icon && <img src={icon} alt="" style={{ width: '16px', height: '16px' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{link}</span>
          </div>
        </div>
        {cover && (
          <div style={{ width: '200px', flexShrink: 0 }}>
            {/* 이미지 CDN이 사용자 네트워크(광고 차단 DNS 등)에서 막히면 깨진 아이콘 대신 커버 영역을 접는다 */}
            <img src={cover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
          </div>
        )}
      </div>
    </a>
  );
};

// API 응답을 react-notion이 기대하는 형식으로 변환
// API: { [id]: { value: { value: { type, ... } } } }
// react-notion 기대: { [id]: { value: { type, ... } } }
function normalizeBlockMap(rawData) {
  const normalized = {};
  for (const [id, block] of Object.entries(rawData)) {
    if (block?.value?.value) {
      normalized[id] = { value: block.value.value, role: block.value.role };
    } else {
      normalized[id] = block;
    }
  }
  return normalized;
}

const NOTION_PAGE_ID = '2057b718d8f080908500e3ff5ef25594';
const CACHE_KEY = `notion-cache:${NOTION_PAGE_ID}`;
const MAX_RETRIES = 3;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

async function fetchWithRetry(url, retries = MAX_RETRIES) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      lastErr = err;
      if (attempt < retries) {
        await sleep(500 * Math.pow(2, attempt));
      }
    }
  }
  throw lastErr;
}

function App() {
  const [response, setResponse] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staleFromCache, setStaleFromCache] = useState(false);

  useEffect(() => {
    let cached = null;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) cached = JSON.parse(raw);
    } catch (_) {}

    fetchWithRetry(`https://notion-api.splitbee.io/v1/page/${NOTION_PAGE_ID}`)
      .then((resJson) => {
        const normalized = normalizeBlockMap(resJson);
        setResponse(normalized);
        setLoading(false);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(normalized));
        } catch (_) {}
      })
      .catch((err) => {
        if (cached) {
          setResponse(cached);
          setStaleFromCache(true);
          setLoading(false);
        } else {
          setError(err.message);
          setLoading(false);
        }
      });
  }, [])

  if (loading) return <div style={{padding:'20px'}}>Loading...</div>;
  if (error) return (
    <div style={{padding:'20px',color:'red'}}>
      Error: {error}
      <div style={{marginTop:'8px',fontSize:'12px',color:'#666'}}>
        Notion API에 접근할 수 없습니다. 새로고침을 시도해 주세요.
      </div>
    </div>
  );

  return (
    <div className="App">
      {staleFromCache && (
        <div style={{padding:'8px 16px',background:'#fff3cd',color:'#856404',fontSize:'12px',borderBottom:'1px solid #ffeeba'}}>
          ⚠️ 최신 데이터를 불러오지 못해 이전에 저장된 내용을 보여줍니다. 새로고침을 시도해 주세요.
        </div>
      )}
      <NotionRenderer
        blockMap={response}
        fullPage={true}
        customBlockComponents={{
          bookmark: ({ blockValue }) => <BookmarkBlock blockValue={blockValue} />
        }}
      />
    </div>
  );
}

export default App;
